import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShoppingCart, TriangleAlert, AlertCircle, CheckCircle2 } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

const FORMULAS = {
  reorder_level: "Reorder Level Based",
  sale_based:    "Sale Based (30-day avg × lead time)",
  pre_order:     "Pre-Order Based",
  moq:           "Minimum Order Quantity Based",
  manual:        "Manual",
};

const STATUS = {
  Critical: { icon: TriangleAlert, cls: "text-destructive bg-destructive/10 border-destructive/30" },
  Low:      { icon: AlertCircle,   cls: "text-yellow-500 bg-yellow-500/10 border-yellow-500/30" },
  Adequate: { icon: CheckCircle2,  cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
};

export default function ReorderPurchase() {
  const navigate = useNavigate();
  const [step, setStep] = useState("filter"); // filter | list
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({ supplier_id: "", company_id: "", category: "", group: "", formula: "reorder_level" });
  const [rows, setRows] = useState([]);
  const [chosenSup, setChosenSup] = useState({}); // item_id -> supplier_id override (from rate comparison badge click)
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/vendors"), api.get("/companies"), api.get("/categories"), api.get("/groups")])
      .then(([v, c, cat, g]) => { setVendors(v.data); setCompanies(c.data); setCategories(cat.data); setGroups(g.data); })
      .catch(e => toast.error(formatErr(e)));
  }, []); // eslint-disable-line

  // Companies mapped to selected supplier only (fallback empty)
  const supplierCompanies = useMemo(() => {
    if (!filters.supplier_id) return companies;
    const v = vendors.find(x => x.id === filters.supplier_id);
    const ids = v?.company_ids || [];
    return companies.filter(c => ids.includes(c.id));
  }, [filters.supplier_id, vendors, companies]);

  // Reset company on supplier change if not mapped
  useEffect(() => {
    if (filters.supplier_id && filters.company_id) {
      const ok = supplierCompanies.some(c => c.id === filters.company_id);
      if (!ok) setFilters(f => ({ ...f, company_id: "" }));
    }
  }, [filters.supplier_id, filters.company_id, supplierCompanies]);

  const proceed = async () => {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const r = await api.get(`/reorder/suggest?${p}`);
      if (!r.data.rows.length) { toast.warning("No items match the current filters"); return; }
      setRows(r.data.rows.map(x => ({ ...x, final_qty: x.suggested_qty })));
      setStep("list");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const supplierBlockMsg = filters.supplier_id && supplierCompanies.length === 0
    ? "No companies linked to this supplier" : null;

  const setQty = (id, v) => setRows(rs => rs.map(r => r.item_id === id ? { ...r, final_qty: Math.max(0, Number(v) || 0) } : r));

  // Auto-include every row whose Suggested Reorder Qty is > 0 — no manual selection needed
  const includedRows = rows.filter(r => (Number(r.final_qty) || 0) > 0);
  const companyGroups = useMemo(() => {
    const g = {};
    rows.forEach(r => { const c = r.company || "No Company"; (g[c] = g[c] || []).push(r); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);
  const pg = usePagination(companyGroups, filters);

  const bulkCreate = async () => {
    if (!includedRows.length) return;
    const rowsPayload = includedRows.map(r => {
      const sid = chosenSup[r.item_id] || r.preferred_supplier_id || filters.supplier_id;
      const chosenRate = (r.rate_comparison || []).find(x => x.supplier_id === sid)?.rate;
      return { item_id: r.item_id, qty: r.final_qty, supplier_id: sid, rate: chosenRate || r.previous_purchase_rate || 0 };
    });
    const missing = rowsPayload.filter(r => !r.supplier_id);
    if (missing.length) { toast.error(`${missing.length} item(s) have no supplier — click a rate badge to pick one, or set a Preferred Supplier`); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/reorders", { formula: filters.formula, rows: rowsPayload });
      toast.success(`Created ${data.created} Reorder${data.created === 1 ? "" : "s"}: ${data.reorders.map(r => r.reorder_number).join(", ")}`);
      setStep("filter");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (step === "filter") {
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transactions · Retail Purchase</p>
            <h1 className="font-display text-2xl font-bold" data-testid="reorder-filter-title">Reorder Suggestion</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/transactions?type=retail_purchase")} data-testid="reorder-back-btn" className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/> Back to list</Button>
          </div>
        </div>
        <div className="border border-border bg-card rounded-sm p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Supplier</Label>
              <Select value={filters.supplier_id} onValueChange={(v) => setFilters(f => ({ ...f, supplier_id: v }))}>
                <SelectTrigger data-testid="filter-supplier" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="All suppliers"/></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
              <Select value={filters.company_id} onValueChange={(v) => setFilters(f => ({ ...f, company_id: v }))} disabled={!!supplierBlockMsg}>
                <SelectTrigger data-testid="filter-company" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="All companies"/></SelectTrigger>
                <SelectContent>{supplierCompanies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {supplierBlockMsg && <div data-testid="supplier-block-msg" className="text-[11px] text-yellow-500 mt-1">{supplierBlockMsg}</div>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="filter-category" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="All categories"/></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
              <Select value={filters.group} onValueChange={(v) => setFilters(f => ({ ...f, group: v }))}>
                <SelectTrigger data-testid="filter-group" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="All groups"/></SelectTrigger>
                <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Reorder Formula *</Label>
              <Select value={filters.formula} onValueChange={(v) => setFilters(f => ({ ...f, formula: v }))}>
                <SelectTrigger data-testid="filter-formula" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.entries(FORMULAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button data-testid="reorder-proceed-btn" onClick={proceed} className="rounded-sm text-xs uppercase tracking-wider bg-primary text-primary-foreground">Proceed <ArrowRight className="w-3 h-3 ml-1"/></Button>
          </div>
        </div>
      </div>
    );
  }

  // Reorder list step
  const total = includedRows.reduce((a, r) => a + r.final_qty * (r.previous_purchase_rate || 0), 0);
  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transactions · Retail Purchase · Reorder List</p>
          <h1 className="font-display text-2xl font-bold" data-testid="reorder-list-title">Reorder List</h1>
          <div className="text-xs text-muted-foreground mt-1" data-testid="reorder-formula-label">Formula: <span className="text-primary font-semibold">{FORMULAS[filters.formula]}</span></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setStep("filter")} data-testid="reorder-change-filter" className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/> Change filters</Button>
          <Button onClick={bulkCreate} disabled={saving || includedRows.length === 0} data-testid="reorder-bulk-create" className="rounded-sm text-xs uppercase tracking-wider bg-primary text-primary-foreground">
            <ShoppingCart className="w-3 h-3 mr-1"/> Create Reorder · {includedRows.length} item{includedRows.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item</th><th className="text-left p-3">Part No</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Current Stock</th><th className="text-right p-3">MOQ</th>
              <th className="text-right p-3">Suggested / Final Qty</th>
              <th className="text-right p-3">Prev. Purch Rate</th>
              <th className="text-left p-3">Supplier Rate Comparison</th>
              <th className="text-left p-3">Preferred Supplier</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(([comp, crs]) => [
              <tr key={`grp-${comp}`} data-testid={`ro-company-${comp}`} className="bg-secondary/70 border-t border-border">
                <td colSpan={10} className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">{comp} <span className="text-muted-foreground font-normal normal-case tracking-normal">· {crs.length} item{crs.length === 1 ? "" : "s"}</span></td>
              </tr>,
              ...crs.map(r => {
              const minRate = r.rate_comparison?.length ? Math.min(...r.rate_comparison.map(x => x.rate).filter(v => v > 0)) : 0;
              return (
                <tr key={r.item_id} data-testid={`reorder-row-${r.item_id}`} className={`border-t border-border ${(Number(r.final_qty) || 0) > 0 ? "bg-primary/5" : "hover:bg-secondary/50"}`}>
                  <td className="p-3">{r.item_name}</td>
                  <td className="p-3 font-mono text-xs text-primary">{r.part_number || "-"}</td>
                  <td className="p-3 text-xs">{r.category || "-"}</td>
                  <td className="p-3 text-right font-mono">{r.current_stock}</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{r.min_order_qty}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <span className="text-[10px] text-muted-foreground font-mono">{r.suggested_qty}→</span>
                      <Input data-testid={`reorder-qty-${r.item_id}`} type="number" min="0" value={r.final_qty} onChange={(e) => setQty(r.item_id, e.target.value)} className="h-8 w-20 rounded-sm bg-secondary border-border text-xs text-right"/>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">{r.previous_purchase_rate ? inr(r.previous_purchase_rate) : "—"}</td>
                  <td className="p-3">
                    {r.rate_comparison?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {r.rate_comparison.map(x => {
                          const activeSid = chosenSup[r.item_id] || r.preferred_supplier_id || filters.supplier_id;
                          const isChosen = x.supplier_id === activeSid;
                          const isLowest = x.rate === minRate && x.rate > 0;
                          return (
                            <button key={x.supplier_id} type="button" data-testid={`rate-comp-${r.item_id}-${x.supplier_id}`}
                              onClick={() => setChosenSup(s => ({ ...s, [r.item_id]: x.supplier_id }))}
                              title={`${x.supplier_name} — click to use for this PO`}
                              className={`px-2 py-0.5 rounded-sm text-[10px] font-mono border transition-colors ${
                                isChosen ? "bg-primary/20 border-primary text-primary font-bold ring-1 ring-primary" :
                                isLowest ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500 font-bold hover:bg-emerald-500/25" :
                                           "bg-secondary border-border text-muted-foreground hover:bg-secondary/70"
                              }`}>
                              {isChosen ? "✓ " : ""}{x.supplier_name.slice(0, 12)}: {inr(x.rate)}
                            </button>
                          );
                        })}
                      </div>
                    ) : <span className="text-xs text-muted-foreground">no history</span>}
                  </td>
                  <td className="p-3 text-xs">{r.preferred_supplier_name || <span className="text-yellow-500">— set in Item Master</span>}</td>
                  <td className="p-3"><StatusBadge s={r.stock_status}/></td>
                </tr>
              );
              }),
            ])}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
      <div className="text-right text-xs text-muted-foreground mt-3" data-testid="reorder-total">Reorder value (qty &gt; 0 items): <span className="font-mono text-primary">{inr(total)}</span></div>
    </div>
  );
}

const StatusBadge = ({ s }) => {
  const cfg = STATUS[s] || STATUS.Adequate;
  const Ico = cfg.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border ${cfg.cls}`}><Ico className="w-3 h-3"/>{s}</span>;
};

